import React from 'react';
import { Moon, Wifi, WifiOff, CheckCircle, XCircle } from 'lucide-react';

type StatusData = {
  device: string;
  connection: string;
  policy: string;
  lastUserLogin: string;
  lastUserLoginAgo: string;
  nextPatchWindow: string;
  lastDisconnected: string;
  lastDisconnectedAgo: string;
  restartAfterPatch: string;
  requiresRestart: string;
};

const sampleStatus: StatusData = {
  device: 'Not Ready',
  connection: 'Disconnected',
  policy: 'Compliant',
  lastUserLogin: '2025-02-04 5:23 AM GMT',
  lastUserLoginAgo: '8 days ago',
  nextPatchWindow: 'Unscheduled',
  lastDisconnected: '2025-02-04 5:23 AM GMT',
  lastDisconnectedAgo: '7 days ago',
  restartAfterPatch: 'No',
  requiresRestart: 'No',
};

const StatusItem = ({ label, value, extra }: { label: string; value: string; extra?: string }) => (
  <div className="py-4 flex justify-between items-start py-1 text-sm">
    <span className="text-gray-400">{label}</span>
    <div className="text-right">
      <span className="text-white">{value}</span>
      {extra && <span className="text-red-400 text-xs ml-2">{extra}</span>}
    </div>
  </div>
);

const InfoStatusCard: React.FC = ({ deviceData }) => {
  return (
    <div className="bg-[#1C1C1D] text-white  p-6 w-full shadow-md">
      <h2 className="text-lg font-semibold mb-4">Status</h2>

      {/* Status Icons */}
      <div className="flex justify-between mb-6">
        {/* Device */}
        <div className="flex flex-col items-center">
          <div className={`rounded-full p-3 mb-2 ${deviceData?.isAgentReady ? 'bg-green-600' : 'bg-red-600'}`}>
            {deviceData?.isAgentReady ? <CheckCircle className="text-white" size={24} /> : <Moon className="text-white" size={24} />}
          </div>
          <p className="text-sm text-white">Device</p>
          <p className="text-xs text-gray-400">{deviceData?.isAgentReady ? 'Ready' : 'Not Ready'}</p>
        </div>

        {/* Connection */}

        <div className="flex flex-col items-center">
          <div className={`rounded-full p-3 mb-2 ${deviceData?.isAgentActive ? 'bg-green-900' : 'bg-red-900'}`}>
            {deviceData?.isAgentActive ? <Wifi className="text-green-500" size={24} /> : <WifiOff className="text-red-500" size={24} />}
          </div>
          <p className="text-sm text-white">Connection</p>
          <p className={`text-xs ${deviceData?.isAgentActive ? 'text-green-400' : 'text-red-400'}`}>
            {deviceData?.isAgentActive ? 'Connected' : 'Disconnected'}
          </p>
        </div>

        {/* Policy */}
        <div className="flex flex-col items-center">
          <div className={`rounded-full p-3 mb-2 ${deviceData?.isPolicyComplaint ? 'bg-green-800' : 'bg-red-800'}`}>
            {deviceData?.isPolicyComplaint ? (
              <CheckCircle className="text-green-400" size={24} />
            ) : (
              <XCircle className="text-red-400" size={24} />
            )}
          </div>
          <p className="text-sm text-white">Policy</p>
          <p className={`text-xs ${deviceData?.isPolicyComplaint ? 'text-green-400' : 'text-red-400'}`}>
            {deviceData?.isPolicyComplaint ? 'Compliant' : 'Non-Compliant'}
          </p>
        </div>
      </div>

      {/* Status Fields */}
      <div className="divide-y divide-gray-700">
        {/* <StatusItem label="Last User Login" value={sampleStatus.lastUserLogin} extra={sampleStatus.lastUserLoginAgo} /> */}
        {/* <StatusItem label="Next Patch Window" value={sampleStatus.nextPatchWindow} /> */}
        <StatusItem
          label="Last Disconnected"
          value={deviceData?.lastHeartbeatReceived ? new Date(deviceData?.lastHeartbeatReceived).toLocaleString() : 'N/A'}
        />
        {/* <StatusItem label="Restart After Patch" value={sampleStatus.restartAfterPatch} /> */}
        <StatusItem label="Device Requires Restart" value={deviceData?.isRestratRequired ? 'Yes' : 'No'} />
        <StatusItem label="Agent Version" value={deviceData?.agentVersion ?? '-'} />
        <StatusItem label="Agent Logon As" value={deviceData?.agentLogonAs ?? '-'} />
        <StatusItem label="Agent Running Under" value={deviceData?.agentRunningUnder ?? '-'} />
      </div>
    </div>
  );
};

export default InfoStatusCard;
